import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

// Each subsystem maps to a real backend probe.
// status: "checking" | "operational" | "degraded"
const SYSTEMS = [
  {
    id: "api",
    name: "Editorial API",
    desc: "Public API serving interviews, articles, and directory.",
    probe: () => api.get("/episodes"),
  },
  {
    id: "auth",
    name: "Authentication",
    desc: "Member sign-in, 2FA, and JWT issuance.",
    // Hitting /auth/me without a token returns 401 — that proves the route is up.
    probe: () => api.get("/auth/me").catch((e) => {
      if (e?.response?.status === 401) return { ok: true };
      throw e;
    }),
  },
  {
    id: "funding",
    name: "Funding Portal",
    desc: "Application intake and AI funding agents.",
    probe: () => api.get("/payments/rails"),
  },
  {
    id: "partners",
    name: "Partners Directory",
    desc: "Partnership and press inquiry pipeline.",
    probe: () => api.get("/members"),
  },
  {
    id: "email",
    name: "Email Delivery",
    desc: "Notification delivery via Resend.",
    // No public health probe for Resend; piggy-back on the API root so we
    // don't show false-negative red on subsystems we can't introspect.
    probe: () => api.get("/"),
  },
  {
    id: "cdn",
    name: "Media CDN",
    desc: "Image and video asset distribution.",
    probe: () => fetch("/campaigns/concept-a.mp4", { method: "HEAD" }).then((r) => {
      if (!r.ok) throw new Error(`CDN HTTP ${r.status}`);
      return r;
    }),
  },
];

export default function SystemStatus() {
  const [statuses, setStatuses] = useState(
    Object.fromEntries(SYSTEMS.map((s) => [s.id, "checking"]))
  );
  const [lastChecked, setLastChecked] = useState(new Date());

  useEffect(() => {
    let cancelled = false;
    SYSTEMS.forEach((s) => {
      s.probe()
        .then(() => {
          if (!cancelled) setStatuses((prev) => ({ ...prev, [s.id]: "operational" }));
        })
        .catch(() => {
          if (!cancelled) setStatuses((prev) => ({ ...prev, [s.id]: "degraded" }));
        });
    });
    setLastChecked(new Date());
    return () => { cancelled = true; };
  }, []);

  const degradedCount = Object.values(statuses).filter((v) => v === "degraded").length;
  const checkingCount = Object.values(statuses).filter((v) => v === "checking").length;
  const allOk = degradedCount === 0 && checkingCount === 0;
  const stillChecking = checkingCount > 0 && degradedCount === 0;

  return (
    <div data-testid="status-page" className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-32 pb-24">
      <div className="mb-12 reveal">
        <p className="eyebrow mb-4">System Status</p>
        <h1 className="font-display text-6xl md:text-7xl mb-6 leading-[0.95]">
          {stillChecking && (
            <>Running <em style={{ color: "var(--ink-muted)" }}>diagnostics</em>…</>
          )}
          {!stillChecking && allOk && (
            <>All systems <em style={{ color: "var(--brass)" }}>operational</em>.</>
          )}
          {!stillChecking && !allOk && degradedCount === 1 && (
            <>One service <em style={{ color: "#E08585" }}>degraded</em>.</>
          )}
          {!stillChecking && !allOk && degradedCount > 1 && (
            <>{degradedCount} services <em style={{ color: "#E08585" }}>degraded</em>.</>
          )}
        </h1>
        <p className="text-base" style={{ color: "var(--ink-muted)" }}>
          Live infrastructure status, refreshed on each page load. Each row probes its subsystem independently. For incidents, contact <a href="mailto:support@wealthfirstinc.tv" className="brass-link">support@wealthfirstinc.tv</a>.
        </p>
      </div>

      <hr className="hairline mb-10" />

      <div className="space-y-3">
        {SYSTEMS.map((s) => {
          const st = statuses[s.id];
          return (
            <div
              key={s.id}
              className="flex items-center justify-between p-6"
              style={{ border: "1px solid var(--hairline)" }}
              data-testid={`status-${s.id}`}
              data-status={st}
            >
              <div>
                <p className="font-display text-xl mb-1">{s.name}</p>
                <p className="text-xs" style={{ color: "var(--ink-muted)" }}>{s.desc}</p>
              </div>
              <div className="flex items-center gap-2">
                {st === "checking" && (
                  <>
                    <Loader2 size={18} className="animate-spin" style={{ color: "var(--ink-muted)" }} />
                    <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--ink-muted)" }}>Checking…</span>
                  </>
                )}
                {st === "operational" && (
                  <>
                    <CheckCircle2 size={18} style={{ color: "#7AB774" }} />
                    <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "#7AB774" }}>Operational</span>
                  </>
                )}
                {st === "degraded" && (
                  <>
                    <AlertCircle size={18} style={{ color: "#E08585" }} />
                    <span className="text-xs uppercase tracking-[0.18em]" style={{ color: "#E08585" }}>Degraded</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center mt-12 text-xs uppercase tracking-[0.22em]" style={{ color: "var(--ink-faint)" }}>
        Last checked · {lastChecked.toLocaleString()}
      </p>
    </div>
  );
}
